/**
 * The skip list, given the lifetime it always needed.
 *
 * ⛔ THE BUG THIS EXISTS FOR. `renderer.dismissed` and index.ts's `excludedIds`
 * lived on the SDK INSTANCE, and Parikara is an SPA that remounts the widget on
 * every route change. Measured 7 days to 2026-09-01: 9,996 impression-dedup
 * violations across 2,066 visitors (the dedup map is per-instance with a 30-min
 * window, so a repeat inside 30 minutes IS a remount), and 1,150 render-path
 * impressions of a pick the same visitor had already dismissed — 364 visitors,
 * 1,118 of them more than 30s after the skip, so not an in-flight race. Owner
 * screenshot, 2026-09-01: «Факел–Краснодар» back on the pill after a skip.
 *
 * Keyed by the SESSION COOKIE VALUE, not a fixed key: `bc_session_id` has a
 * 30-minute max-age, so a new session gets a new key and an empty list with no
 * timestamp bookkeeping. sessionStorage is the right store for the same reason
 * hide-state.ts gives — it dies with the tab and survives reloads.
 */
const PREFIX = "bc_skips_";

export class SkipMemory {
  private mem: string[];

  constructor(private sessionId: string, private cap = 100) {
    this.mem = this.read();
    this.sweepStrandedKeys();
  }

  /** The session id this instance is keyed on — read by the diag strip so the
   *  line it prints (`skips (session ...)`) names the key it actually reads
   *  and writes, not whatever `client.getSessionId()` returns live. The
   *  cookie hard-expires at 30 minutes with no refresh-on-read
   *  (`getCookieId`), so on a longer page view the two can drift apart. */
  getSessionId(): string {
    return this.sessionId;
  }

  ids(): string[] {
    return [...this.mem];
  }

  add(id: string): void {
    if (!id) return;
    // Read-modify-write, not a blind overwrite of `this.mem`. A remount can
    // leave the OUTGOING instance still alive; if it writes after the new
    // instance already has, a blind overwrite clobbers the new instance's
    // skip. Remounts are the normal case here (9,996 dedup violations across
    // 2,066 visitors in 7 days), so this window is not theoretical.
    const stored = this.read();
    const merged = [...stored];
    for (const existing of this.mem) if (!merged.includes(existing)) merged.push(existing);
    if (merged.includes(id)) {
      this.mem = merged;
      return;
    }
    merged.push(id);
    // Oldest out first: a skip from twenty picks ago has been answered by the
    // twenty picks since, and the backend caps `dismissed_ids` at 200 anyway.
    while (merged.length > this.cap) merged.shift();
    this.mem = merged;
    this.write();
  }

  private key(): string {
    return PREFIX + this.sessionId;
  }

  private read(): string[] {
    try {
      const raw = window.sessionStorage.getItem(this.key());
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : [];
    } catch {
      // Private mode throws on ACCESS, not just on write; corrupt JSON lands
      // here too. An unreadable store must never cost the user their widget.
      return [];
    }
  }

  private write(): void {
    try {
      window.sessionStorage.setItem(this.key(), JSON.stringify(this.mem));
    } catch {
      /* memory-only for the rest of this page view */
    }
  }

  /** Every session rotation leaves a `bc_skips_<old-id>` entry behind for the
   *  life of the tab — sessionStorage never expires it. Sweep anything under
   *  our prefix that isn't the current key. Never allowed to throw: a failed
   *  sweep must cost nothing beyond a stranded key that was already there. */
  private sweepStrandedKeys(): void {
    try {
      const mine = this.key();
      const stale: string[] = [];
      for (let i = 0; i < window.sessionStorage.length; i++) {
        const k = window.sessionStorage.key(i);
        if (k && k.startsWith(PREFIX) && k !== mine) stale.push(k);
      }
      for (const k of stale) window.sessionStorage.removeItem(k);
    } catch {
      /* best-effort only */
    }
  }
}
